import { Controller } from '@hotwired/stimulus'
import { createPopper } from '@popperjs/core'
import '../../assets/stylesheets/components/reference_chip.css'

// Renders @[gid|name] object references as chips and keeps them current: hydrates each chip's display name
// from /ai/content_references and marks references whose target no longer resolves as broken. In the editor,
// chips are created as widget nodes by markdown_editor_controller; here we only hydrate. In readonly viewer
// mode we also DOM-scan the rendered HTML and wrap reference tokens in chips first.
const CONTAINER_SELECTOR = '.toastui-editor-ww-container .toastui-editor-contents'
const FALLBACK_CONTAINER_SELECTOR = '.toastui-editor-contents'
const RETRY_INTERVAL = 300
const MAX_RETRIES = 15
const REFERENCE_TOKEN_G = /@\[(gid:\/\/[^\]|]+)(?:\|([^\]]*))?\]/g
// Display labels per reference type (SOP is an acronym, not "Sop").
const TYPE_LABELS = { form: 'Form', article: 'Article', sop: 'SOP' }

export default class extends Controller {
  static values = { readonly: { type: Boolean, default: false } }

  connect() {
    this.handleChipClick = this.onChipClick.bind(this)
    this.handleDismiss = this.dismissPopover.bind(this)
    // A chip inserted from the @ menu is built from the token, which has no status. Hydrate again
    // once the editor has turned that token into a chip.
    this.handleConvert = () => {
      if (this.containerElement) this.hydrate(this.containerElement)
    }
    this.element.addEventListener('markdown-editor:convert-references', this.handleConvert)
    this.initWithRetry()
  }

  disconnect() {
    this.removePopover()
    this.element.removeEventListener('markdown-editor:convert-references', this.handleConvert)
    this.containerElement?.removeEventListener('click', this.handleChipClick, true)
  }

  initWithRetry(retries = 0) {
    const container = this.findContainer()
    if (!container) {
      if (retries < MAX_RETRIES) setTimeout(() => this.initWithRetry(retries + 1), RETRY_INTERVAL)
      return
    }
    this.containerElement = container
    container.addEventListener('click', this.handleChipClick, true)
    if (this.readonlyValue) this.renderChipsInViewer(container)
    this.hydrate(container)
  }

  findContainer() {
    const next = this.element.nextElementSibling
    return next?.querySelector(CONTAINER_SELECTOR) || next?.querySelector(FALLBACK_CONTAINER_SELECTOR)
  }

  // Viewer mode: wrap @[gid|name] text tokens in reference chips.
  renderChipsInViewer(container) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: node =>
        node.parentElement.closest('.reference-chip, code, pre') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
    })
    const nodes = []
    let node
    while ((node = walker.nextNode())) {
      REFERENCE_TOKEN_G.lastIndex = 0
      if (REFERENCE_TOKEN_G.test(node.textContent)) nodes.push(node)
    }
    for (let i = nodes.length - 1; i >= 0; i--) this.replaceTextWithChips(nodes[i])
  }

  replaceTextWithChips(textNode) {
    const text = textNode.textContent
    const fragment = document.createDocumentFragment()
    let lastIndex = 0
    REFERENCE_TOKEN_G.lastIndex = 0
    let match
    while ((match = REFERENCE_TOKEN_G.exec(text)) !== null) {
      if (match.index > lastIndex) fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)))
      fragment.appendChild(this.buildChip(match[1], match[2] || ''))
      lastIndex = REFERENCE_TOKEN_G.lastIndex
    }
    if (lastIndex < text.length) fragment.appendChild(document.createTextNode(text.substring(lastIndex)))
    if (fragment.childNodes.length > 0) textNode.parentNode.replaceChild(fragment, textNode)
  }

  buildChip(gid, name) {
    const chip = document.createElement('span')
    chip.className = 'reference-chip'
    chip.dataset.gid = gid
    chip.dataset.name = name
    chip.textContent = name || gid
    return chip
  }

  // Fetch current names for all referenced gids; refresh labels and mark unresolved references as broken.
  async hydrate(container) {
    const chips = Array.from(container.querySelectorAll('.reference-chip'))
    if (chips.length === 0) return

    const gids = [...new Set(chips.map(chip => chip.dataset.gid).filter(Boolean))]
    let resolved
    try {
      const query = gids.map(gid => `gids[]=${encodeURIComponent(gid)}`).join('&')
      const response = await fetch(`/ai/content_references?${query}`, { headers: { Accept: 'application/json' } })
      if (!response.ok) return
      resolved = new Map((await response.json()).map(ref => [ref.gid, ref]))
    } catch (error) {
      console.error('Failed to hydrate reference chips:', error)
      return
    }

    for (const chip of chips) {
      const ref = resolved.get(chip.dataset.gid)
      if (ref) {
        chip.textContent = ref.name
        chip.dataset.name = ref.name
        chip.dataset.type = ref.type
        chip.classList.remove('reference-chip--broken')
        this.applyStatus(chip, ref.status)
      } else {
        // Unresolved: keep the last-known snapshot name, flag it as deleted.
        chip.dataset.broken = 'true'
        chip.classList.add('reference-chip--broken')
      }
    }
  }

  // Disabled (or otherwise off) targets stay resolvable. The chip turns the error color and the
  // popup's warning bar says so. A missing status clears a previous one.
  applyStatus(chip, status) {
    if (status) {
      chip.dataset.status = status
      chip.classList.add('reference-chip--unavailable')
    } else {
      delete chip.dataset.status
      chip.classList.remove('reference-chip--unavailable')
    }
  }

  // --- Popover ---

  onChipClick(event) {
    const chip = event.target.closest('.reference-chip')
    if (!chip) return
    event.preventDefault()
    event.stopPropagation()
    this.showPopover(chip)
  }

  showPopover(chip) {
    this.removePopover()

    this.popover = document.createElement('div')
    this.popover.className = 'reference-chip-popover'
    this.popover.innerHTML = this.buildPopoverHTML(chip)
    document.body.appendChild(this.popover)

    this.popperInstance = createPopper(chip, this.popover, {
      placement: 'bottom-start',
      modifiers: [
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] } },
        { name: 'preventOverflow', options: { padding: 16 } }
      ]
    })

    requestAnimationFrame(() => document.addEventListener('click', this.handleDismiss))
  }

  buildPopoverHTML(chip) {
    const parsed = this.parseGid(chip.dataset.gid)
    const broken = chip.dataset.broken === 'true'
    const label = this.typeLabel(chip.dataset.type || parsed?.type)
    const name = chip.dataset.name || chip.textContent
    const status = chip.dataset.status

    let html = `
      <div class="reference-chip-popover__header">
        <span class="reference-chip-popover__name">${this.esc(name)}</span>
        <span class="reference-chip-popover__badges">
          <span class="reference-chip-popover__type">${this.esc(label)}</span>
        </span>
      </div>`

    if (broken) {
      html += `<div class="reference-chip-popover__section reference-chip-popover__section--warning">
        This reference no longer exists — remove or replace it.</div>`
    } else {
      if (status) {
        const noun = label === 'SOP' ? 'SOP' : label.toLowerCase()
        html += `<div class="reference-chip-popover__section reference-chip-popover__section--warning">
          This ${this.esc(noun)} is ${this.esc(status.toLowerCase())}.</div>`
      }
      const path = this.editPath(parsed)
      if (path) {
        html += `<div class="reference-chip-popover__footer">
          <a href="${this.esc(path)}" target="_blank" rel="noopener" class="reference-chip-popover__link">
            Open ${this.esc(label)} &rarr;</a></div>`
      }
    }

    return html
  }

  typeLabel(type) {
    if (!type) return 'Reference'
    return TYPE_LABELS[type] || (type[0].toUpperCase() + type.slice(1))
  }

  // gid://shipped-base/<Model>/<id> -> { model, id, type }
  parseGid(gid) {
    const match = String(gid || '').match(/gid:\/\/[^/]+\/([^/]+)\/(\d+)/)
    if (!match) return null
    return { model: match[1], id: match[2], type: match[1].toLowerCase() }
  }

  editPath(parsed) {
    if (!parsed) return null
    switch (parsed.model) {
      case 'Form': return `/ai/forms/${parsed.id}/edit`
      case 'SOP': return `/ai/sops/${parsed.id}/edit`
      case 'Article': return `/ai/knowledge/articles/${parsed.id}/edit`
      default: return null
    }
  }

  dismissPopover(event) {
    if (this.popover && !this.popover.contains(event.target) && !event.target.closest('.reference-chip')) {
      this.removePopover()
    }
  }

  removePopover() {
    if (this.popperInstance) {
      this.popperInstance.destroy()
      this.popperInstance = null
    }
    if (this.popover) {
      this.popover.remove()
      this.popover = null
    }
    document.removeEventListener('click', this.handleDismiss)
  }

  esc(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
